import React, { useState } from "react";
import SubPageLayout from "../../../components/shared/SubPageLayout";

const AllEvents = () => {
  // State for filters
  const [filters, setFilters] = useState({
    type: "all",
    dateRange: "all",
    searchQuery: "",
    eventStatus: "all",
  });

  // State for sorting
  const [sortConfig, setSortConfig] = useState({
    key: "date",
    direction: "asc",
  });

  // State for pagination
  const [currentPage, setCurrentPage] = useState(1);
  const eventsPerPage = 10;

  // Mock events data (in a real app, this would come from an API)
  const [events, setEvents] = useState([
    // Upcoming Events
    {
      id: 1,
      name: "Retirement Planning Seminar",
      date: "2024-04-15T14:00:00",
      type: "In-Person",
      subType: "Seminar",
      status: "Scheduled",
      metrics: {
        rsvps: 45,
        pendingTasks: 3,
        attendance: null,
      },
    },
    {
      id: 2,
      name: "Investment Strategy Webinar",
      date: "2024-04-20T10:00:00",
      type: "Virtual",
      subType: "External Webinar",
      status: "In Progress",
      metrics: {
        rsvps: 120,
        pendingTasks: 5,
        attendance: null,
      },
    },
    {
      id: 3,
      name: "Client Appreciation Night",
      date: "2024-05-01T18:00:00",
      type: "In-Person",
      subType: "Networking",
      status: "Scheduled",
      metrics: {
        rsvps: 75,
        pendingTasks: 8,
        attendance: null,
      },
    },
    // Past Events
    {
      id: 4,
      name: "Tax Planning Workshop",
      date: "2024-02-15T15:00:00",
      type: "In-Person",
      subType: "Educational",
      status: "Completed",
      metrics: {
        rsvps: 60,
        pendingTasks: 0,
        attendance: 52,
        feedback: 4.8,
        followUps: 15,
      },
    },
    {
      id: 5,
      name: "Market Outlook 2024",
      date: "2024-01-20T11:00:00",
      type: "Virtual",
      subType: "External Webinar",
      status: "Completed",
      metrics: {
        rsvps: 200,
        pendingTasks: 0,
        attendance: 168,
        feedback: 4.6,
        followUps: 45,
      },
    },
    {
      id: 6,
      name: "Estate Planning Basics",
      date: "2024-03-10T14:00:00",
      type: "Virtual",
      subType: "Internal Webinar",
      status: "Completed",
      metrics: {
        rsvps: 85,
        pendingTasks: 0,
        attendance: 72,
        feedback: 4.7,
        followUps: 28,
      },
    },
    {
      id: 7,
      name: "Social Security Strategies",
      date: "2024-02-28T13:00:00",
      type: "In-Person",
      subType: "Seminar",
      status: "Completed",
      metrics: {
        rsvps: 90,
        pendingTasks: 0,
        attendance: 82,
        feedback: 4.9,
        followUps: 35,
      },
    },
  ]);

  const handleFilter = (filterType, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterType]: value,
    }));
    setCurrentPage(1); // Reset to first page when filtering
  };

  const handleSort = (key) => {
    setSortConfig((prevConfig) => ({
      key,
      direction:
        prevConfig.key === key && prevConfig.direction === "asc"
          ? "desc"
          : "asc",
    }));
  };

  // Filter events based on all criteria
  const filteredEvents = events.filter((event) => {
    // Filter by event status
    if (filters.eventStatus !== "all") {
      if (filters.eventStatus === "Completed" && event.status !== "Completed")
        return false;
      if (filters.eventStatus === "Scheduled" && event.status !== "Scheduled")
        return false;
      if (
        filters.eventStatus === "In Progress" &&
        event.status !== "In Progress"
      )
        return false;
    }

    // Filter by type
    if (filters.type !== "all" && event.type !== filters.type) return false;

    // Filter by search query
    if (
      filters.searchQuery &&
      !event.name.toLowerCase().includes(filters.searchQuery.toLowerCase())
    )
      return false;

    // Filter by date range
    if (filters.dateRange !== "all") {
      const eventDate = new Date(event.date);
      const now = new Date();
      const week = 7 * 24 * 60 * 60 * 1000;
      const month = 30 * 24 * 60 * 60 * 1000;
      const quarter = 3 * month;

      switch (filters.dateRange) {
        case "week":
          if (Math.abs(eventDate.getTime() - now.getTime()) > week)
            return false;
          break;
        case "month":
          if (Math.abs(eventDate.getTime() - now.getTime()) > month)
            return false;
          break;
        case "quarter":
          if (Math.abs(eventDate.getTime() - now.getTime()) > quarter)
            return false;
          break;
        default:
          break;
      }
    }

    return true;
  });

  // Sort events
  const sortedEvents = [...filteredEvents].sort((a, b) => {
    if (sortConfig.key === "date") {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return sortConfig.direction === "asc" ? dateA - dateB : dateB - dateA;
    }
    return 0;
  });

  // Calculate pagination
  const indexOfLastEvent = currentPage * eventsPerPage;
  const indexOfFirstEvent = indexOfLastEvent - eventsPerPage;
  const currentEvents = sortedEvents.slice(indexOfFirstEvent, indexOfLastEvent);
  const totalPages = Math.ceil(sortedEvents.length / eventsPerPage);

  return (
    <SubPageLayout
      breadcrumbItems={[
        { label: "Advisor Portal", path: "/advisor-portal" },
        { label: "Events", path: "/advisor-portal/events" },
        { label: "All Events", path: "#" },
      ]}
      title="All Events"
      subtitle="View and manage all your events"
    >
      <div className="space-y-6">
        {/* Filters Section */}
        <div className="bg-surface-default dark:bg-surface-default rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-6">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div className="relative flex-1">
              <input
                type="text"
                placeholder="Search events..."
                value={filters.searchQuery}
                onChange={(e) => handleFilter("searchQuery", e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-gray-100 border-0 rounded-xl text-sm text-gray-700 dark:text-gray-300
                  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-surface-default dark:bg-surface-default transition-colors"
              />
              <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                <svg
                  className="w-5 h-5 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <select
                value={filters.eventStatus}
                onChange={(e) => handleFilter("eventStatus", e.target.value)}
                className="px-4 py-2 bg-gray-100 border-0 rounded-xl text-sm text-gray-700 dark:text-gray-300
                  focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
              >
                <option value="all">All Status</option>
                <option value="Scheduled">Scheduled</option>
                <option value="In Progress">In Progress</option>
                <option value="Completed">Completed</option>
              </select>

              <select
                value={filters.type}
                onChange={(e) => handleFilter("type", e.target.value)}
                className="px-4 py-2 bg-gray-100 border-0 rounded-xl text-sm text-gray-700 dark:text-gray-300
                  focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
              >
                <option value="all">All Types</option>
                <option value="Virtual">Virtual</option>
                <option value="In-Person">In-Person</option>
              </select>

              <select
                value={filters.dateRange}
                onChange={(e) => handleFilter("dateRange", e.target.value)}
                className="px-4 py-2 bg-gray-100 border-0 rounded-xl text-sm text-gray-700 dark:text-gray-300
                  focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
              >
                <option value="all">All Dates</option>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
                <option value="quarter">This Quarter</option>
              </select>

              {Object.values(filters).some(
                (value) => value !== "all" && value !== "",
              ) && (
                <button
                  onClick={() => {
                    setFilters({
                      type: "all",
                      dateRange: "all",
                      searchQuery: "",
                      eventStatus: "all",
                    });
                  }}
                  className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:text-gray-100 transition-colors"
                >
                  Clear Filters
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Events List */}
        <div className="bg-surface-default dark:bg-surface-default rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100 dark:border-gray-800">
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 dark:text-gray-500">
                    <button
                      onClick={() => handleSort("date")}
                      className="flex items-center gap-2"
                    >
                      Date
                      {sortConfig.key === "date" && (
                        <svg
                          className={`w-4 h-4 transform ${
                            sortConfig.direction === "desc" ? "rotate-180" : ""
                          }`}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 15l7-7 7 7"
                          />
                        </svg>
                      )}
                    </button>
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 dark:text-gray-500">
                    Event Name
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 dark:text-gray-500">
                    Type
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 dark:text-gray-500">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 dark:text-gray-500">
                    Metrics
                  </th>
                  <th className="px-6 py-4 text-right text-sm font-medium text-gray-500 dark:text-gray-500">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {currentEvents.map((event) => (
                  <tr key={event.id} className="group hover:bg-gray-50 dark:hover:bg-gray-800">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-500">
                      {new Date(event.date).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {event.name}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-500">
                        {event.subType}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-3 py-1 text-sm rounded-full font-medium ${
                          event.type === "Virtual"
                            ? "bg-blue-100 text-blue-800"
                            : "bg-emerald-100 text-emerald-800"
                        }`}
                      >
                        {event.type}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-3 py-1 text-sm rounded-full font-medium ${
                          event.status === "Completed"
                            ? "bg-green-100 text-green-800"
                            : event.status === "In Progress"
                              ? "bg-blue-100 text-blue-800"
                              : "bg-amber-100 text-amber-800"
                        }`}
                      >
                        {event.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-500">
                        <div className="flex items-center gap-1">
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={1.5}
                              d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                            />
                          </svg>
                          <span>{event.metrics.rsvps}</span>
                        </div>
                        {event.status === "Completed" && (
                          <>
                            <div className="flex items-center gap-1">
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={1.5}
                                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                              </svg>
                              <span>{event.metrics.attendance}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <svg
                                className="w-4 h-4 text-yellow-400"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                              </svg>
                              <span>{event.metrics.feedback}</span>
                            </div>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button className="text-blue-600 hover:text-blue-900">
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 dark:border-gray-800">
              <div className="text-sm text-gray-500 dark:text-gray-500">
                Showing {indexOfFirstEvent + 1} to{" "}
                {Math.min(indexOfLastEvent, sortedEvents.length)} of{" "}
                {sortedEvents.length} events
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={currentPage === 1}
                  className={`p-2 rounded-lg ${
                    currentPage === 1
                      ? "text-gray-400 cursor-not-allowed"
                      : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
                  }`}
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                </button>
                {[...Array(totalPages)].map((_, index) => (
                  <button
                    key={index + 1}
                    onClick={() => setCurrentPage(index + 1)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium ${
                      currentPage === index + 1
                        ? "bg-blue-600 text-white"
                        : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
                    }`}
                  >
                    {index + 1}
                  </button>
                ))}
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                  className={`p-2 rounded-lg ${
                    currentPage === totalPages
                      ? "text-gray-400 cursor-not-allowed"
                      : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
                  }`}
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </SubPageLayout>
  );
};

export default AllEvents;
